import React, { createContext, useState, useCallback } from 'react';
import * as scheduleService from '../services/scheduleService';
import { downloadJSON } from '../utils/helpers';

export const ScheduleContext = createContext(null);

export function ScheduleProvider({ children }) {
  const [weeks, setWeeks] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [gridData, setGridData] = useState(null);
  const [allShows, setAllShows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchWeeks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await scheduleService.getWeeks();
      const weeksList = Array.isArray(data) ? data : data.data || data.weeks || [];
      setWeeks(weeksList);
      setCurrentWeek((prev) => (prev ? prev : weeksList[0] || null));
    } catch (err) {
      console.error('Failed to fetch weeks:', err);
      setWeeks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGrid = useCallback(async (weekId) => {
    if (!weekId) return;
    setLoading(true);
    try {
      const raw = await scheduleService.getGrid(weekId);
      const grid = raw?.data || raw;
      const days = grid?.days || {};
      const entries = [];
      const showsMap = {};

      Object.keys(days).forEach((dayKey) => {
        const dayNum = parseInt(dayKey, 10);
        const slots = days[dayKey]?.slots || [];
        slots.forEach((slot) => {
          entries.push({
            id: slot.id,
            show_id: slot.show_id,
            episode_id: slot.episode_id || null,
            day: dayNum,
            time: (slot.start_time || '').substring(0, 5),
            duration: slot.duration ?? 30,
            broadcast_type: slot.broadcast_type,
            season: slot.season,
            episode: slot.episode,
            episode_title: slot.episode_title || null,
          });
          if (slot.show_id && !showsMap[slot.show_id]) {
            showsMap[slot.show_id] = {
              id: slot.show_id,
              name: slot.show_name,
              color: slot.show_color,
            };
          }
        });
      });

      setGridData({
        entries,
        shows: Object.values(showsMap),
      });
    } catch (err) {
      console.error('Failed to fetch grid:', err);
      setGridData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const moveEntry = useCallback(async (entryId, newDay, newTime) => {
    try {
      await scheduleService.moveEntry(entryId, {
        day_of_week: newDay,
        start_time: newTime,
      });
      if (currentWeek) {
        await fetchGrid(currentWeek.id);
      }
    } catch (err) {
      throw err;
    }
  }, [currentWeek, fetchGrid]);

  const fetchAllShows = useCallback(async () => {
    try {
      const data = await scheduleService.getAllShows();
      const list = Array.isArray(data) ? data : data.data || data.shows || [];
      setAllShows(list);
    } catch (err) {
      console.error('Failed to fetch shows:', err);
      setAllShows([]);
    }
  }, []);

  const addEntry = useCallback(async (data) => {
    const entry = await scheduleService.createEntry(data);
    if (currentWeek) {
      await fetchGrid(currentWeek.id);
    }
    return entry;
  }, [currentWeek, fetchGrid]);

  const updateEntry = useCallback(async (id, data) => {
    await scheduleService.updateEntry(id, data);
    if (currentWeek) {
      await fetchGrid(currentWeek.id);
    }
  }, [currentWeek, fetchGrid]);

  const deleteEntry = useCallback(async (id) => {
    await scheduleService.deleteEntry(id);
    if (currentWeek) {
      await fetchGrid(currentWeek.id);
    }
  }, [currentWeek, fetchGrid]);

  const exportGrid = useCallback(async (weekId) => {
    const id = weekId || currentWeek?.id;
    if (!id) return;
    const data = await scheduleService.exportGrid(id);
    const week = weeks.find((w) => w.id === id);
    const filename = week
      ? `grille-${week.start_date || week.id}.json`
      : `grille-export-${id}.json`;
    downloadJSON(data, filename);
  }, [currentWeek, weeks]);

  const createWeek = useCallback(async () => {
    let nextMonday;
    if (weeks.length > 0) {
      const latest = weeks.reduce((a, b) =>
        (a.start_date || '') > (b.start_date || '') ? a : b
      );
      const lastStart = new Date(latest.start_date + 'T00:00:00');
      nextMonday = new Date(lastStart);
      nextMonday.setDate(nextMonday.getDate() + 7);
    } else {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
      nextMonday = new Date(today);
      nextMonday.setDate(today.getDate() + diff);
    }
    const startDate = nextMonday.toISOString().slice(0, 10);
    const endDate = new Date(nextMonday);
    endDate.setDate(endDate.getDate() + 6);
    const endStr = endDate.toISOString().slice(0, 10);

    const result = await scheduleService.createWeek({
      start_date: startDate,
      end_date: endStr,
    });
    await fetchWeeks();
    const newWeek = { id: result.id, start_date: startDate, end_date: endStr };
    setCurrentWeek(newWeek);
    return result;
  }, [weeks, fetchWeeks]);

  const value = {
    weeks,
    currentWeek,
    setCurrentWeek,
    gridData,
    allShows,
    loading,
    fetchWeeks,
    fetchGrid,
    fetchAllShows,
    moveEntry,
    addEntry,
    updateEntry,
    deleteEntry,
    exportGrid,
    createWeek,
  };

  return (
    <ScheduleContext.Provider value={value}>{children}</ScheduleContext.Provider>
  );
}
